/**
 * Reading object-detection annotations out of an uploaded zip.
 *
 * Supports the two formats people actually export: **YOLO/Ultralytics** (one
 * `.txt` per image, normalised centre-form) and **COCO** (one JSON, absolute
 * pixel corner-form). Pascal VOC is recognised so the UI can say it is not
 * supported yet, rather than importing a folder of images and silently dropping
 * every box.
 *
 * Everything here is pure over paths and text, so the whole format-detection
 * matrix is testable without a zip.
 *
 * The output is always the shape LabelFlow stores:
 *
 *     { classIndex, centerX, centerY, width, height }   all 0..1
 *
 * Four things this has to get right, each of which fails silently otherwise:
 *
 * - **Coordinates are CLAMPED to 0..1.** Training normalises with
 *   `val / denom if val > 1 else val` — a magnitude heuristic, not a unit
 *   conversion. A YOLO file that rounds a full-width box to `1.0000001` would
 *   be read as a pixel value and divided by the image width, collapsing the box
 *   to nothing. Exporters emit values a hair outside the range routinely.
 *
 * - **A COCO `category_id` is NOT a class index.** COCO ids are arbitrary,
 *   conventionally 1-based, and need not be contiguous — the real COCO set runs
 *   to id 90 for 80 classes. Using one as an index puts every box on the wrong
 *   class, or off the end of the list.
 *
 * - **An EMPTY `.txt` is meaningful.** It is a negative sample: an image that
 *   genuinely contains none of the classes. It is not a parse failure.
 *
 * - **A 5-column line is a box; 7-or-more odd columns is a POLYGON.** Both live
 *   in `labels/<stem>.txt` with identical names, so only the column count tells
 *   them apart — Ultralytics itself distinguishes them by shape.
 */

/** Directory and file names that carry class names, in priority order. */
const NAME_SOURCES = ['data.yaml', 'data.yml', 'notes.json', 'obj.names', 'classes.txt'];

const SPLIT_WORDS = new Set(['train', 'training', 'val', 'valid', 'validation', 'test', 'testing']);

const lastSegment = (path) => String(path || '').split('/').filter(Boolean).pop() || '';
const stemOf = (path) => lastSegment(path).replace(/\.[^.]*$/, '');
const lower = (text) => String(text || '').toLowerCase();

/** Clamp into 0..1. See the note about training's magnitude heuristic. */
export const clamp01 = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return null;
  return Math.min(1, Math.max(0, number));
};

/**
 * The label path Ultralytics would look for, given an image path.
 *
 * It replaces the LAST `/images/` segment with `/labels/` — not the parent
 * directory — so all three layouts in the wild resolve:
 *   images/train/x.jpg -> labels/train/x.txt
 *   train/images/x.jpg -> train/labels/x.txt
 *   images/x.jpg       -> labels/x.txt
 */
export const labelPathFor = (imagePath) => {
  const path = String(imagePath || '');
  const marker = '/images/';
  const at = path.lastIndexOf(marker);
  const swapped =
    at >= 0
      ? `${path.slice(0, at)}/labels/${path.slice(at + marker.length)}`
      : path.replace(/^images\//, 'labels/');
  return swapped.replace(/\.[^./]*$/, '.txt');
};

/**
 * Pair images with their label files.
 *
 * Tries the Ultralytics path substitution first; anything still unpaired falls
 * back to matching on basename, which covers hand-rolled layouts. The fallback
 * is only used when a stem is UNIQUE among the leftovers, because two
 * `001.txt` in different folders cannot be told apart.
 */
export const pairImagesToLabels = (imagePaths = [], labelPaths = []) => {
  const labels = new Set(labelPaths);
  const pairs = [];
  const unpairedImages = [];

  imagePaths.forEach((imagePath) => {
    const direct = labelPathFor(imagePath);
    if (labels.has(direct)) {
      pairs.push({ imagePath, labelPath: direct });
      return;
    }
    unpairedImages.push(imagePath);
  });

  const used = new Set(pairs.map((pair) => pair.labelPath));
  const leftovers = labelPaths.filter((path) => !used.has(path));
  const byStem = new Map();
  leftovers.forEach((path) => {
    const stem = stemOf(path);
    if (byStem.has(stem)) byStem.set(stem, null); // ambiguous
    else byStem.set(stem, path);
  });

  const stillUnpaired = [];
  unpairedImages.forEach((imagePath) => {
    const match = byStem.get(stemOf(imagePath));
    if (match) {
      pairs.push({ imagePath, labelPath: match });
      byStem.set(stemOf(imagePath), null);
    } else {
      stillUnpaired.push(imagePath);
    }
  });

  return { pairs, unpairedImages: stillUnpaired };
};

/**
 * Parse one YOLO label file.
 *
 * Returns `{ boxes, kind, skipped }` where `kind` is `boxes`, `segments` or
 * `empty`. Segments are reported, never converted — a polygon dataset is a
 * segmentation dataset and importing its points as boxes would be wrong.
 */
export const parseYoloLabel = (text) => {
  const lines = String(text || '')
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line && !line.startsWith('#'));

  if (!lines.length) return { boxes: [], kind: 'empty', skipped: 0 };

  const boxes = [];
  let segments = 0;
  let skipped = 0;

  lines.forEach((line) => {
    const parts = line.split(/\s+/);
    // 5 = class cx cy w h. 6 = the same plus a confidence column, which a
    // prediction dump carries. 7+ and odd = a polygon.
    if (parts.length === 5 || parts.length === 6) {
      const classIndex = Number.parseInt(parts[0], 10);
      const centerX = clamp01(parts[1]);
      const centerY = clamp01(parts[2]);
      const width = clamp01(parts[3]);
      const height = clamp01(parts[4]);
      if (
        !Number.isInteger(classIndex) || classIndex < 0 ||
        centerX === null || centerY === null || width === null || height === null ||
        width <= 0 || height <= 0
      ) {
        skipped += 1;
        return;
      }
      boxes.push({ classIndex, centerX, centerY, width, height });
      return;
    }
    if (parts.length >= 7 && parts.length % 2 === 1) {
      segments += 1;
      return;
    }
    skipped += 1;
  });

  if (segments && !boxes.length) return { boxes: [], kind: 'segments', skipped };
  return { boxes, kind: boxes.length ? 'boxes' : 'empty', skipped: skipped + segments };
};

/**
 * Class names from a `data.yaml`.
 *
 * Deliberately NOT a YAML parser — only `names` and `nc` are read, and `names`
 * arrives in three shapes in the wild: an inline list, a block list, and a
 * mapping keyed by index (which may be quoted). A mapping's keys are the class
 * indices and need not start at 0 in a malformed file, so they are honoured
 * rather than assumed.
 */
export const parseDataYaml = (text) => {
  const source = String(text || '');
  const names = [];

  const inline = source.match(/^\s*names\s*:\s*\[(.*)\]\s*$/m);
  if (inline) {
    inline[1]
      .split(',')
      .map((part) => part.trim().replace(/^['"]|['"]$/g, ''))
      .filter(Boolean)
      .forEach((name) => names.push(name));
    return { names, nc: names.length };
  }

  const lines = source.split(/\r?\n/);
  const start = lines.findIndex((line) => /^\s*names\s*:\s*$/.test(line));
  if (start >= 0) {
    const mapping = new Map();
    for (let i = start + 1; i < lines.length; i += 1) {
      const line = lines[i];
      if (!line.trim()) continue;
      // A non-indented line ends the block.
      if (!/^\s/.test(line)) break;
      const item = line.match(/^\s*-\s*(.+?)\s*$/);
      if (item) {
        names.push(item[1].replace(/^['"]|['"]$/g, ''));
        continue;
      }
      const keyed = line.match(/^\s*['"]?(\d+)['"]?\s*:\s*(.+?)\s*$/);
      if (keyed) {
        mapping.set(Number(keyed[1]), keyed[2].replace(/^['"]|['"]$/g, ''));
        continue;
      }
      break;
    }
    if (mapping.size) {
      const highest = Math.max(...mapping.keys());
      for (let i = 0; i <= highest; i += 1) names.push(mapping.get(i) || `class ${i + 1}`);
    }
  }

  const ncMatch = source.match(/^\s*nc\s*:\s*(\d+)\s*$/m);
  return { names, nc: ncMatch ? Number(ncMatch[1]) : names.length };
};

/** `classes.txt` / `obj.names`: one name per line, index = line number. */
export const parseNamesList = (text) =>
  String(text || '')
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line && !line.startsWith('#'));

/** Label Studio's `notes.json`: `{categories: [{id, name}]}`. */
export const parseNotesJson = (text) => {
  try {
    const parsed = JSON.parse(text);
    const categories = Array.isArray(parsed?.categories) ? parsed.categories : [];
    return categories
      .slice()
      .sort((a, b) => (a.id || 0) - (b.id || 0))
      .map((entry) => String(entry.name || '').trim())
      .filter(Boolean);
  } catch (error) {
    return [];
  }
};

/**
 * Parse a COCO detection JSON into the same normalised shape as YOLO.
 *
 * `category_id` is mapped through the `categories` array's own order — never
 * used as an index. Boxes are converted from absolute top-left pixels to
 * normalised centre form using each image's own declared width/height.
 */
export const parseCoco = (text) => {
  let parsed;
  try {
    parsed = typeof text === 'string' ? JSON.parse(text) : text;
  } catch (error) {
    return null;
  }
  if (!parsed || !Array.isArray(parsed.images) || !Array.isArray(parsed.categories)) return null;
  if (!Array.isArray(parsed.annotations)) return null;

  const categories = parsed.categories
    .slice()
    .sort((a, b) => (Number(a.id) || 0) - (Number(b.id) || 0));
  const names = categories.map((entry, index) => String(entry.name || `class ${index + 1}`).trim());
  const indexByCategoryId = new Map();
  categories.forEach((entry, index) => indexByCategoryId.set(Number(entry.id), index));

  const imagesById = new Map();
  parsed.images.forEach((image) => {
    if (!image || image.id == null) return;
    imagesById.set(Number(image.id), {
      fileName: String(image.file_name || ''),
      width: Number(image.width) || 0,
      height: Number(image.height) || 0,
      boxes: [],
    });
  });

  let skipped = 0;
  parsed.annotations.forEach((annotation) => {
    const image = imagesById.get(Number(annotation?.image_id));
    const bbox = annotation?.bbox;
    if (!image || !Array.isArray(bbox) || bbox.length < 4) {
      skipped += 1;
      return;
    }
    if (!image.width || !image.height) {
      skipped += 1;
      return;
    }
    const classIndex = indexByCategoryId.get(Number(annotation.category_id));
    if (classIndex == null) {
      skipped += 1;
      return;
    }
    // COCO bbox is [x, y, w, h] with (x, y) the TOP-LEFT corner, in pixels.
    const [x, y, w, h] = bbox.map(Number);
    if (!Number.isFinite(x) || !Number.isFinite(y) || !(w > 0) || !(h > 0)) {
      skipped += 1;
      return;
    }
    const box = {
      classIndex,
      centerX: clamp01((x + w / 2) / image.width),
      centerY: clamp01((y + h / 2) / image.height),
      width: clamp01(w / image.width),
      height: clamp01(h / image.height),
    };
    if (box.width <= 0 || box.height <= 0) {
      skipped += 1;
      return;
    }
    image.boxes.push(box);
  });

  // COCO file_name may carry a subdirectory; index by basename too.
  const byFileName = new Map();
  imagesById.forEach((image) => {
    if (!image.fileName) return;
    byFileName.set(image.fileName, image);
    byFileName.set(lastSegment(image.fileName), image);
  });

  return { names, byFileName, skipped, imageCount: imagesById.size };
};

/**
 * What annotation format, if any, this archive is in — from paths alone.
 *
 * Returns `{ format, labelPaths, nameFiles, cocoPaths, root }`. `format` is
 * `yolo`, `coco`, `voc` or null.
 */
export const detectAnnotationFormat = (paths = []) => {
  const all = paths.filter(Boolean);
  const labelPaths = all.filter((path) => /\.txt$/i.test(path) && !NAME_SOURCES.includes(lower(lastSegment(path))));
  const nameFiles = all.filter((path) => NAME_SOURCES.includes(lower(lastSegment(path))));
  const cocoPaths = all.filter((path) => /\.json$/i.test(path) && lower(lastSegment(path)) !== 'notes.json');
  const xmlPaths = all.filter((path) => /\.xml$/i.test(path));

  // A labels/ directory alongside images is the strongest YOLO signal; a
  // data.yaml alone is not, because a classification zip can carry one.
  const hasLabelsDir = all.some((path) => /(^|\/)labels\//i.test(path));
  const hasYaml = nameFiles.some((path) => /\.ya?ml$/i.test(path));

  if (labelPaths.length && (hasLabelsDir || hasYaml)) {
    return { format: 'yolo', labelPaths, nameFiles, cocoPaths, xmlPaths };
  }
  if (cocoPaths.length) return { format: 'coco', labelPaths, nameFiles, cocoPaths, xmlPaths };
  if (xmlPaths.length >= 2) return { format: 'voc', labelPaths, nameFiles, cocoPaths, xmlPaths };
  return { format: null, labelPaths, nameFiles, cocoPaths, xmlPaths };
};

/** Split folders are not classes; used when naming a detection import. */
export const isSplitWord = (word) => SPLIT_WORDS.has(lower(word));

/**
 * Read the small metadata files an annotated archive carries.
 *
 * Called once, at ATTACH time, so the plan card can show the dataset's real
 * class names before the user commits to anything. Only tiny text files are
 * read here — never an image.
 *
 * `readText(path)` is injected so this is testable without a zip.
 */
export const readAnnotationMeta = async (analysis, readText) => {
  const info = (analysis && analysis.annotation) || { format: null, nameFiles: [], cocoPaths: [] };
  const result = { format: info.format, names: [], nameSource: null, coco: null, error: null };
  if (!info.format) return result;

  if (info.format === 'coco') {
    // Prefer the conventional Roboflow name, else the first JSON.
    const ordered = [...info.cocoPaths].sort((a, b) => {
      const score = (path) => (/_annotations\.coco\.json$/i.test(path) ? 0 : 1);
      return score(a) - score(b);
    });
    for (let i = 0; i < ordered.length; i += 1) {
      try {
        // eslint-disable-next-line no-await-in-loop
        const parsed = parseCoco(await readText(ordered[i]));
        if (parsed) {
          result.coco = parsed;
          result.names = parsed.names;
          result.nameSource = ordered[i];
          return result;
        }
      } catch (error) {
        result.error = error && error.message;
      }
    }
    // JSON files that are not COCO after all — this is not a detection archive.
    result.format = null;
    return result;
  }

  // YOLO: class names live in one of four files, in priority order.
  const byPriority = [...(info.nameFiles || [])].sort(
    (a, b) => NAME_SOURCES.indexOf(lower(lastSegment(a))) - NAME_SOURCES.indexOf(lower(lastSegment(b))),
  );
  for (let i = 0; i < byPriority.length; i += 1) {
    const path = byPriority[i];
    const base = lower(lastSegment(path));
    try {
      // eslint-disable-next-line no-await-in-loop
      const text = await readText(path);
      let names = [];
      if (base === 'data.yaml' || base === 'data.yml') names = parseDataYaml(text).names;
      else if (base === 'notes.json') names = parseNotesJson(text);
      else names = parseNamesList(text);
      if (names.length) {
        result.names = names;
        result.nameSource = path;
        return result;
      }
    } catch (error) {
      result.error = error && error.message;
    }
  }
  return result;
};

/**
 * Make sure there is a name for every class index that actually appears.
 *
 * A names file can disagree with the labels — a truncated `classes.txt`, or an
 * exporter that wrote 1-based indices. Inventing the missing names is far
 * better than dropping the boxes that use them, and the card says it happened.
 */
export const reconcileClassNames = (names = [], highestIndex = -1) => {
  const out = names.map((name, index) => String(name || `class ${index + 1}`).trim());
  const invented = [];
  for (let index = out.length; index <= highestIndex; index += 1) {
    out.push(`class ${index + 1}`);
    invented.push(index);
  }
  return { names: out, invented };
};

/**
 * Turn a scanned archive plus its metadata into the items a detection import
 * uploads: `{path, name, annotations, originalWidth, originalHeight}`.
 *
 * `readBytes(path)` and `readText(path)` are injected, so the whole thing is
 * testable without a zip.
 *
 * Note the asymmetry between the two formats, which is worth the branch: a COCO
 * document already states every image's width and height, so nothing needs to
 * be read. A YOLO archive says nothing about size, and its coordinates are
 * normalised — so the size is needed only for `originalWidth`/`originalHeight`,
 * which the labeling canvas draws against. That is read from the image HEADER
 * rather than by decoding it.
 */
export const buildDetectionItems = async ({
  analysis,
  meta,
  readBytes,
  readText,
  readSize,
  limit,
}) => {
  const images = (analysis.images || []).slice(0, limit || (analysis.images || []).length);
  const stats = {
    withBoxes: 0, withoutBoxes: 0, boxes: 0,
    unpaired: 0, segmentFiles: 0, badLines: 0, missingSize: 0, highestClassIndex: -1,
  };
  const items = [];

  if (meta.format === 'coco' && meta.coco) {
    images.forEach((image) => {
      const record =
        meta.coco.byFileName.get(image.path) ||
        meta.coco.byFileName.get(image.name) ||
        null;
      const boxes = record ? record.boxes : [];
      if (!record) stats.unpaired += 1;
      boxes.forEach((box) => {
        if (box.classIndex > stats.highestClassIndex) stats.highestClassIndex = box.classIndex;
      });
      stats.boxes += boxes.length;
      if (boxes.length) stats.withBoxes += 1;
      else stats.withoutBoxes += 1;
      items.push({
        path: image.path,
        name: image.name,
        boxes,
        // COCO states the size, so nothing has to be read.
        originalWidth: record ? record.width : 0,
        originalHeight: record ? record.height : 0,
      });
      if (!record || !record.width || !record.height) stats.missingSize += 1;
    });
    return { items, stats };
  }

  const { pairs, unpairedImages } = pairImagesToLabels(
    images.map((image) => image.path),
    (analysis.annotation && analysis.annotation.labelPaths) || [],
  );
  const labelByImage = new Map(pairs.map((pair) => [pair.imagePath, pair.labelPath]));
  stats.unpaired = unpairedImages.length;

  for (let i = 0; i < images.length; i += 1) {
    const image = images[i];
    let boxes = [];
    const labelPath = labelByImage.get(image.path);
    if (labelPath) {
      try {
        // eslint-disable-next-line no-await-in-loop
        const parsed = parseYoloLabel(await readText(labelPath));
        if (parsed.kind === 'segments') stats.segmentFiles += 1;
        stats.badLines += parsed.skipped;
        boxes = parsed.boxes;
      } catch (error) {
        stats.badLines += 1;
      }
    }
    boxes.forEach((box) => {
      if (box.classIndex > stats.highestClassIndex) stats.highestClassIndex = box.classIndex;
    });
    stats.boxes += boxes.length;
    if (boxes.length) stats.withBoxes += 1;
    else stats.withoutBoxes += 1;

    let size = null;
    try {
      // eslint-disable-next-line no-await-in-loop
      size = readSize(await readBytes(image.path));
    } catch (error) {
      size = null;
    }
    if (!size) stats.missingSize += 1;
    items.push({
      path: image.path,
      name: image.name,
      boxes,
      originalWidth: size ? size.width : 0,
      originalHeight: size ? size.height : 0,
    });
  }

  return { items, stats };
};

/**
 * Attach class ids to the parsed boxes.
 *
 * A YOLO/COCO class INDEX is 0-based and positional; a LabelFlow class `id` is
 * a join key that starts at 1. Keeping them separate until this point means the
 * two numbering schemes never get confused.
 */
export const withClassIds = (items = [], classes = []) =>
  items.map((item) => ({
    ...item,
    annotations: (item.boxes || [])
      .map((box) => {
        const entry = classes[box.classIndex];
        if (!entry) return null;
        return {
          classId: entry.id,
          centerX: box.centerX,
          centerY: box.centerY,
          width: box.width,
          height: box.height,
        };
      })
      .filter(Boolean),
  }));
